import React, { Component } from "react";
import Moment from "moment";
import { PageHeader, Table, Tag, DatePicker, Button, Space, Alert, Tooltip } from "antd";
import { ReloadOutlined, ClockCircleOutlined } from "@ant-design/icons";
import CombinedDeliveriesService from "../../../services/combineddeliveriesservice";

const { RangePicker } = DatePicker;
const API_FMT = "YYYY-MM-DD";

// Combined Deliveries: orders the combined-delivery experiment is holding — one or more splits are
// back in store but a sibling is still out, so the whole order is withheld from Ready-for-Invoice
// until every split returns. When the last sibling arrives the order leaves this list and appears
// on Ready-for-Delivery/Invoice automatically. Read-only, auto-refreshes every 60s.
export default class CombinedDeliveries extends Component {
  constructor(props) {
    super(props);
    this.service = new CombinedDeliveriesService();
    this.state = {
      from: Moment().subtract(60, "days"),
      to: Moment(),
      rows: [],
      loading: true,
    };
    this.timer = null;

    this.columns = [
      {
        title: "Order",
        dataIndex: "OrderID",
        key: "OrderID",
        sorter: (a, b) => a.OrderID - b.OrderID,
        render: (text) => <b>{text}</b>,
      },
      {
        title: "Customer",
        dataIndex: "CustomerName",
        key: "CustomerName",
        render: (text, r) => (
          <React.Fragment>
            <div>{r.CustomerName}</div>
            <small style={{ color: "#999" }}>{r.MobileNo}</small>
          </React.Fragment>
        ),
      },
      {
        title: "Store",
        dataIndex: "StoreName",
        key: "StoreName",
      },
      {
        title: "Order Date",
        dataIndex: "OrderDate",
        key: "OrderDate",
        sorter: (a, b) => Moment(a.OrderDate).unix() - Moment(b.OrderDate).unix(),
        render: (t) => (t ? Moment(t).format("DD-MM-YYYY") : "-"),
      },
      {
        title: "Progress",
        key: "progress",
        align: "center",
        sorter: (a, b) => a.SplitsInStore / a.TotalSplits - b.SplitsInStore / b.TotalSplits,
        render: (text, r) => (
          <Tag color="purple" style={{ fontWeight: 600 }}>
            {r.SplitsInStore} / {r.TotalSplits} in store
          </Tag>
        ),
      },
      {
        title: "In Store",
        dataIndex: "InStoreServices",
        key: "InStoreServices",
        render: (t) =>
          (t || "")
            .split(",")
            .filter((s) => s.trim())
            .map((s) => (
              <Tag color="green" key={s} style={{ marginBottom: 2 }}>
                {s.trim()}
              </Tag>
            )),
      },
      {
        title: "Waiting On",
        dataIndex: "PendingServices",
        key: "PendingServices",
        render: (t) =>
          (t || "")
            .split(",")
            .filter((s) => s.trim())
            .map((s) => (
              <Tag color="volcano" key={s} style={{ marginBottom: 2 }}>
                {s.trim()}
              </Tag>
            )),
      },
      {
        title: "Combined Delivery",
        dataIndex: "CombinedDeliveryDate",
        key: "CombinedDeliveryDate",
        align: "center",
        sorter: (a, b) =>
          Moment(a.CombinedDeliveryDate).unix() - Moment(b.CombinedDeliveryDate).unix(),
        render: (t) =>
          t ? (
            <Space size={4}>
              <ClockCircleOutlined style={{ color: "#6a1b9a" }} />
              {Moment(t).format("DD-MM-YYYY")}
            </Space>
          ) : (
            "-"
          ),
      },
    ];
  }

  componentDidMount() {
    this.load();
    this.timer = setInterval(() => this.load(true), 60000);
  }
  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }

  storeIDs() {
    return localStorage.getItem("branchID") || "";
  }

  load = async (silent) => {
    if (!silent) this.setState({ loading: true });
    const rows = await this.service.getPending(
      this.storeIDs(),
      this.state.from.format(API_FMT),
      this.state.to.format(API_FMT)
    );
    this.setState({ rows: Array.isArray(rows) ? rows : [], loading: false });
  };

  onRange = (dates) => {
    if (dates && dates.length === 2) {
      this.setState({ from: dates[0], to: dates[1] }, () => this.load());
    }
  };

  render() {
    const { rows, loading, from, to } = this.state;
    return (
      <div>
        <PageHeader
          title="Combined Deliveries"
          subTitle={`${rows.length} order${rows.length === 1 ? "" : "s"} held`}
          extra={[
            <RangePicker
              key="range"
              format="DD-MM-YYYY"
              value={[from, to]}
              allowClear={false}
              onChange={this.onRange}
            />,
            <Tooltip title="Refresh" key="refresh">
              <Button icon={<ReloadOutlined />} loading={loading} onClick={() => this.load()} />
            </Tooltip>,
          ]}
        />
        <div style={{ padding: "0 24px 24px" }}>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="These orders are waiting for all their services to return to store. Each moves to Ready for Delivery / Invoice automatically once every split is back — nothing to do here."
          />
          <Table
            size="small"
            rowKey="OrderID"
            columns={this.columns}
            dataSource={rows}
            loading={loading}
            pagination={{ pageSize: 20, hideOnSinglePage: true, showSizeChanger: false }}
            locale={{ emptyText: "No combined deliveries pending — nothing is being held right now." }}
          />
        </div>
      </div>
    );
  }
}
